import { useQuery } from '@tanstack/react-query';
import { Card, CardEyebrow, CardTitle, CardContent } from '@bookrcs/ui';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

interface ClientRow {
  id: string;
  name: string | null;
  phoneHash: string;
  optedOut: boolean;
  createdAt: string;
}

function maskHash(hash: string): string {
  if (hash.length < 8) return hash;
  return `${hash.slice(0, 4)}…${hash.slice(-4)}`;
}

export default function ClientsRoute() {
  const { session } = useAuth();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['clients', session?.proId],
    queryFn: () =>
      api<ClientRow[]>('/clients', {
        searchParams: { proId: session?.proId ?? '' },
      }),
    enabled: Boolean(session?.proId),
  });

  const activeCount = (data ?? []).filter((c) => !c.optedOut).length;
  const optedOutCount = (data ?? []).filter((c) => c.optedOut).length;

  return (
    <div className="grid gap-6 max-w-4xl">
      <header className="flex items-baseline justify-between">
        <div>
          <p className="text-[var(--text-muted)] m-0">Clients</p>
          <h1 className="font-serif text-4xl md:text-5xl font-extrabold m-0 mt-1 text-[var(--text)]">
            Ton carnet
          </h1>
        </div>
        {data ? (
          <span className="font-mono text-sm text-[var(--text-faint)]">
            {activeCount} actifs · {optedOutCount} opt-out
          </span>
        ) : null}
      </header>

      <Card>
        <CardEyebrow>Liste consentie</CardEyebrow>
        <CardTitle>{data?.length ?? 0} client(s)</CardTitle>
        <CardContent>
          {isLoading ? <p className="text-[var(--text-muted)]">Chargement…</p> : null}
          {isError ? (
            <p className="text-[var(--urgent)]">
              L'API ne répond pas. Vérifie `@bookrcs/api` (port 4000).
            </p>
          ) : null}
          {data && data.length === 0 ? (
            <p className="text-[var(--text-muted)] italic m-0 mt-2">
              Pas encore de client opt-in. Partage ton lien Insta pour démarrer.
            </p>
          ) : null}
          {data && data.length > 0 ? (
            <table className="w-full mt-2 text-sm">
              <thead className="text-[var(--text-faint)] text-xs uppercase">
                <tr>
                  <th className="text-left font-bold py-2">Nom</th>
                  <th className="text-left font-bold py-2">Téléphone (hash)</th>
                  <th className="text-right font-bold py-2">Opt-in</th>
                  <th className="text-right font-bold py-2">Statut</th>
                </tr>
              </thead>
              <tbody>
                {data.map((c) => (
                  <tr key={c.id} className="border-t border-[var(--border)]">
                    <td className="py-2 font-bold text-[var(--text)]">{c.name ?? '—'}</td>
                    <td className="py-2 font-mono text-xs text-[var(--text-muted)]">
                      {maskHash(c.phoneHash)}
                    </td>
                    <td className="py-2 text-right font-mono text-xs text-[var(--text-faint)]">
                      {new Date(c.createdAt).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="py-2 text-right">
                      {c.optedOut ? (
                        <span className="text-[var(--urgent)] font-bold text-xs uppercase">
                          stop
                        </span>
                      ) : (
                        <span className="text-[var(--success)] font-bold text-xs uppercase">
                          actif
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </CardContent>
      </Card>

      <p className="text-xs text-[var(--text-faint)] italic">
        Téléphones jamais affichés en clair (HMAC-SHA256 + pepper côté serveur). CNIL §
        right-to-erasure 72h via{' '}
        <code className="font-mono text-[var(--text)]">/admin/clients/:id/erase</code>.
      </p>
    </div>
  );
}
